/**
 * Premium Applications -- deliberately a different category from a regular
 * tool: these are the handful of builds on the site with real, multi-
 * step workflows (upload/arrange/configure/assemble), closer to a small
 * application than a single-action tool. Kept as a plain constant Set +
 * data map here (not a repurposed `category` field on tools-data.ts)
 * so tagging a tool into this tier never changes which category page
 * it still belongs on -- Book Writing Toolbox is still a PDF tool first.
 *
 * Candidates considered and deliberately left out: Avatar Builder and
 * Invitation Card Maker were both real contenders by file-complexity,
 * but neither is developed enough yet to carry "application" framing
 * well (Avatar Builder has no clothing/shoes/accessories yet; the
 * invitation maker isn't distinct enough from a template picker) --
 * revisit once they're more complete.
 */
export const FEATURED_APP_IDS = new Set([
  "universal-file-converter",
  "book-writing-toolbox",
  "document-scanner",
  "catalog-maker",
  "resume-builder",
  "magazine-maker",
  "event-suite",
]);

/**
 * Metadata for the Premium Applications hub page and homepage preview boxes.
 * name/description are pulled verbatim from tools-data.ts (not
 * reworded) so this page never says something different about a tool
 * than its own tool page does. iconName maps to the same glyph
 * concept as each app's generated PWA icon (see
 * client/public/icons/multitool/*) so the icon here, the installed
 * app icon, and the tool itself all agree visually.
 */
export interface FeaturedAppMeta {
  id: string;
  name: string;
  description: string;
  iconName: "BookOpen" | "ScanLine" | "LayoutGrid" | "FileUser" | "Newspaper" | "Shuffle" | "CalendarDays";
  accent: "signal" | "pulse";
}

export const FEATURED_APP_META: FeaturedAppMeta[] = [
  {
    id: "universal-file-converter",
    name: "Universal File Converter",
    description: "Drop any file, see every real format it can become — images, PDF, Excel, PowerPoint, and text",
    iconName: "Shuffle",
    accent: "pulse",
  },
  {
    id: "book-writing-toolbox",
    name: "Book Writing Toolbox",
    description: "Write your book directly in a real rich-text editor, preview it instantly with professional typesetting, and download when it's ready",
    iconName: "BookOpen",
    accent: "signal",
  },
  {
    id: "document-scanner",
    name: "Document Scanner",
    description: "Turn a photo of a document into a clean scanned look — crop, straighten, rotate, and save as PDF",
    iconName: "ScanLine",
    accent: "pulse",
  },
  {
    id: "catalog-maker",
    name: "Catalog Maker",
    description: "Assemble product sheets and photos into a complete catalog with cover, price list, and auto-generated Table of Contents",
    iconName: "LayoutGrid",
    accent: "signal",
  },
  {
    id: "resume-builder",
    name: "Resume / CV Builder",
    description: "Create ATS-friendly resumes with templates",
    iconName: "FileUser",
    accent: "pulse",
  },
  {
    id: "magazine-maker",
    name: "Magazine Maker",
    description: "Write articles (text, one picture each) or combine finished PDF, Word and image files into a magazine issue with a cover and an 'In this issue' contents page",
    iconName: "Newspaper",
    accent: "signal",
  },
  {
    id: "event-suite",
    name: "Event Suite",
    description: "Run all your events under one account: one subscription, pick each event type, add a starter plan, and use thirteen products that share one project, from first quote to final report",
    iconName: "CalendarDays",
    accent: "signal",
  },
];

export interface FeaturedAppContent {
  heroImage: string;
  heroTagline: string;
  howToUse: string[];
  tips: string[];
  faq: { question: string; answer: string }[];
}

export const FEATURED_APP_CONTENT: Record<string, FeaturedAppContent> = {
  "universal-file-converter": {
    heroImage: "/blog/universal-file-converter-launch.webp",
    heroTagline: "Drop a file. See every real format it can become.",
    howToUse: [
      "Drop a file — an image (JPG, PNG, WebP), a PDF, a text file, a spreadsheet, or a PowerPoint presentation.",
      "The tool detects what you uploaded and shows only the output formats that genuinely work for that file type — never a dead-end option.",
      "Pick a target format. Each one runs the exact same conversion logic as that format's own dedicated tool elsewhere on the site.",
      "Download the result, or hit \"Start over\" to convert a different file.",
    ],
    tips: [
      "This tool deliberately supports fewer conversions than a full permutation of every format on the site — each one here is a real, working conversion reused from its own dedicated tool, not a new implementation. If a pairing you need isn't offered, the dedicated tool for that specific conversion may still exist elsewhere on the site even if it's not listed here yet.",
      "PDF to Excel only works when the PDF actually contains table-shaped data — a PDF that's just prose won't have anything to extract.",
      "Image to Text uses on-device OCR (the same engine as the standalone OCR tool) — works best on clear, high-contrast text, not stylized fonts or low-resolution photos.",
    ],
    faq: [
      {
        question: "Why doesn't this support every conversion I can think of?",
        answer:
          "Each conversion here reuses a real, already-working implementation from that format's own dedicated tool — this is a router over proven logic, not a second, separate conversion engine. Expanding coverage means building a genuine new conversion first, the same standard as everything else on the site, so what's offered is always something that actually works, not a placeholder.",
      },
      {
        question: "Is this different from using the individual PDF/image/Excel tools directly?",
        answer:
          "Not in what it does — same underlying conversion, same quality of result. What's different is not needing to know in advance which specific tool handles your file; drop it here and see what's actually possible.",
      },
      {
        question: "Are my files uploaded to a server?",
        answer:
          "No — every conversion runs entirely in your browser, the same privacy model as every other tool on the site. Nothing is uploaded anywhere.",
      },
    ],
  },
  "magazine-maker": {
    heroImage: "/blog/magazine-maker-launch.webp",
    heroTagline: "A cover, articles, and a real Table of Contents — assembled automatically.",
    howToUse: [
      "Write articles: type or paste each article (or load a .txt or .md file), add one picture per article, and optionally a cover image.",
      "Combine files: upload finished articles as PDF, Word or image files instead, and name each section.",
      "Arrange articles into your preferred reading order; the contents page and page numbers follow automatically.",
      "Build, check the preview, then download the magazine or newsletter as one PDF.",
    ],
    tips: [
      "A newsletter or internal company magazine benefits from the same layout discipline as a consumer magazine — consistent page numbering reads as more professional than a plain document.",
      "Reordering articles is safe — the contents page updates to match.",
      "Markdown works in article text: # headings, **bold**, *italic*, - bullets and 1. numbered lists. Emoji can't be printed in the PDF and are left out.",
      "In Combine files mode, mixing formats (a Word article next to a photographed page) works — everything converts to matching pages first.",
    ],
    faq: [
      {
        question: "Is this different from Book Writing Toolbox?",
        answer:
          "Yes, genuinely different tools now: Magazine Maker lays out a multi-article issue: in Write articles mode you type or paste articles (text, one picture each, or a .txt/.md file) into 1 to 3 columns with a cover and contents page, and in Combine files mode it merges finished PDF, Word or image files into one issue. Book Writing Toolbox is a chapter editor for books, with professional typesetting (real hyphenation, drop caps). Pick Magazine Maker for a newsletter or multi-article issue; pick Book Writing Toolbox for a book.",
      },
      {
        question: "Can I use this for an internal company newsletter, not just a public magazine?",
        answer:
          "Yes — that's a common real use. An internal newsletter benefits from the same cover + Table of Contents + consistent pagination structure as a public-facing one.",
      },
      {
        question: "What file types can each article be?",
        answer:
          "It depends on the mode. Write articles takes typed or pasted text, one picture per article, and .txt or .md files. Combine files takes PDF, Word (.docx), .txt and image files, mixed within the same issue; each converts to consistent pages before assembly.",
      },
    ],
  },
  "catalog-maker": {
    heroImage: "/blog/catalog-maker-launch.webp",
    heroTagline: "Products in, a laid-out catalog PDF out — with page numbers and a Table of Contents.",
    howToUse: [
      "Enter your products (name, price, description) and upload a photo for each.",
      "Group products into categories if you want a segmented catalog rather than one flat list.",
      "Catalog Maker lays out each product automatically and generates a Table of Contents.",
      "Download the finished catalog as a print- or share-ready PDF.",
    ],
    tips: [
      "Consistent photo aspect ratios make the auto-layout look noticeably more polished — crop product photos to a similar shape before uploading if you can.",
      "Categories are optional — skip them for a simple single-list catalog, or use them to split a larger catalog (e.g. by product line) with its own section in the Table of Contents.",
      "Re-ordering products or categories after entering them is safe — page numbers and the Table of Contents recompute.",
    ],
    faq: [
      {
        question: "How many products can I add?",
        answer:
          "There's no hard cap in the tool itself — practical size is limited by your own patience entering products and by browser memory for very large catalogs (many hundreds of products with photos). For a typical small business catalog (tens to a couple hundred items), it runs fine.",
      },
      {
        question: "Do I need professional product photography?",
        answer:
          "No — any photo works, though consistent lighting and aspect ratio across products will make the finished catalog look more cohesive. The layout engine doesn't require any particular photo quality to function.",
      },
      {
        question: "Can I edit the catalog after generating it?",
        answer:
          "Not the generated PDF directly — go back into Catalog Maker, adjust your products/categories, and regenerate. Since everything runs in your browser and nothing is saved server-side, keep your product list somewhere you can return to if you'll be updating the catalog regularly.",
      },
    ],
  },
  "resume-builder": {
    heroImage: "/blog/resume-builder-launch.webp",
    heroTagline: "Section by section, to an ATS-friendly resume — not a rigid template fight.",
    howToUse: [
      "Fill in your contact info, then add sections for experience, education, and skills.",
      "Reorder sections to match how you want your resume prioritized.",
      "Preview updates live as you type, so you can see the finished layout before downloading.",
      "Download as a PDF formatted to parse cleanly through applicant tracking systems (ATS).",
    ],
    tips: [
      "ATS-friendly means avoiding complex multi-column layouts, text boxes, and graphics that many tracking systems fail to parse correctly — this tool's output is deliberately plain for that reason, not because it couldn't do fancier layouts.",
      "List experience in reverse-chronological order (most recent first) — it's both the ATS-friendly convention and what most human reviewers expect.",
      "Keep bullet points under each role focused on outcomes, not just duties — a resume section is one of the few places on the site where content quality matters as much as the tool's formatting.",
    ],
    faq: [
      {
        question: "What does \"ATS-friendly\" actually mean here?",
        answer:
          "Many companies run resumes through Applicant Tracking Systems that parse the text before a human ever sees it. Complex layouts (multi-column designs, text inside images, decorative graphics) often parse incorrectly or get skipped entirely. This tool's output avoids those specifically so your content reaches a human reviewer intact.",
      },
      {
        question: "Can I save my progress and come back later?",
        answer:
          "Not currently — everything is held in your browser for the session, nothing is saved server-side. Finish in one sitting, or keep your content drafted elsewhere and paste it in when you're ready to generate the final PDF.",
      },
      {
        question: "Is this good for creative fields where a more visual resume is expected?",
        answer:
          "Not really its strength — if you're in a field (design, for example) where a highly visual resume is the norm and ATS parsing matters less, a dedicated design tool will serve you better. This tool is built specifically for the common case where ATS compatibility matters more than visual flair.",
      },
    ],
  },
  "document-scanner": {
    heroImage: "/blog/document-scanner-launch.webp",
    heroTagline: "A photo of a document in, a clean, cropped scan out.",
    howToUse: [
      "Take or upload a photo of a physical document.",
      "The tool detects the document's edges automatically and crops to just the page.",
      "Adjust the crop manually if the auto-detection doesn't get it exactly right, and choose an enhancement mode (e.g. black-and-white for text clarity).",
      "Download the cleaned-up scan as an image or PDF.",
    ],
    tips: [
      "Even, diffuse lighting (not a single direct light or flash) gives the edge-detection algorithm a much easier time — harsh shadows are the most common cause of a bad auto-crop.",
      "Shoot from directly above the document, not at an angle — an angled shot introduces perspective distortion the tool can partially correct but not always perfectly.",
      "For multi-page documents, scan each page separately, then use the Merge PDF tool afterward to combine them into one file.",
    ],
    faq: [
      {
        question: "How is this different from just taking a photo?",
        answer:
          "Automatic edge detection and cropping (so the document fills the frame instead of sitting inside a photo of a desk), plus enhancement modes that push text contrast and reduce background noise/shadows — the output reads as an actual scan, not a photo of paper.",
      },
      {
        question: "Does it work for scanning multiple pages of one document?",
        answer:
          "Each page needs to be scanned individually through the tool, since it processes one photo at a time. Use the site's Merge PDF tool afterward to combine the individual scans into a single multi-page PDF.",
      },
      {
        question: "What if the automatic edge detection gets it wrong?",
        answer:
          "You can manually adjust the crop corners yourself — the auto-detection is a starting point, not the only option, for documents with low contrast against their background or unusual shapes.",
      },
    ],
  },
  "event-suite": {
    heroImage: "/blog/event-suite-launch.webp",
    heroTagline: "Plan any event, from first quote to final report.",
    howToUse: [
      "Sign in and subscribe. The Event Suite is $29.99 per month with a 7-day trial, or Premium at $49.99 per month, or it is open to you through an organization plan.",
      "Choose the kind of event: wedding, birthday, corporate event, conference, exhibition, fundraiser or something else.",
      "Add the starter plan if you want a checklist, a budget split and a running order counted from your event date.",
      "Open the products you need. They all work on one shared project, so guests, sessions, quotes and costs are entered once. Save the project file, or save it to your account (1 GB on every plan), to keep your work.",
      "On Premium, press Publish event to share private links for three installable phone apps: Event Guide for guests, Door Check for door staff and Booth Leads for each exhibitor.",
    ],
    tips: [
      "Start with the starter plan, then change it. The checklist, budget and running order are typical examples, not rules for your event.",
      "Save the project at the end of every session, as a file or to your account. Nothing is kept unless you choose to save or publish it.",
      "Working with a team? An organization plan covers many people at a lower price per seat.",
    ],
    faq: [
      {
        question: "Is there a trial, and is there a day pass?",
        answer: "There is a 7-day trial the first time you subscribe. You are charged when it ends unless you cancel first, and you can cancel any time from the page itself. There is no day pass: the Event Suite is a monthly subscription, built for people who plan events regularly.",
      },
      {
        question: "Does the sitewide plan include it?",
        answer: "No. The Event Suite is its own subscription. Your other plans and trial uses work as they always have for every other tool on the site.",
      },
      {
        question: "Is my event data stored on your servers?",
        answer: "Not unless you choose. Your project stays in your browser tab and is erased when you close it. If you save it to your account, it is kept (up to 1 GB) until you delete it or your plan ends. On Premium, publishing an event also keeps the published event, the door guest list and check-ins, and each booth's leads until you delete them or Premium ends.",
      },
    ],
  },
};
